<template>
  <div :class="$style.container">
    <component :is="ControlledModal">
      <ConfirmationModal
        title="選択肢を削除"
        buttonText="選択肢を全て削除"
        @confirm="submitDeleteAllSelections"
        @close="closeModal"
      >
        <p>
          選択肢を全て削除します、よろしいですか？<br />（※本文は削除されません）
        </p>
      </ConfirmationModal>
    </component>
    <template v-if="selections.length > 0">
      <header :class="[$style.container__header, $style.header]">
        <p :class="$style.header__title">選択肢一覧</p>
        <button
          v-if="isDeleteAllSelectionsAllowed"
          :class="$style.header__delete"
          @click="confirmDeleteAll"
        >
          全て削除
        </button>
      </header>
      <ul :class="[$style.container__contents, $style.selections]">
        <li
          v-for="(selection, index) in modelValue"
          :key="index"
          :class="[$style.selections__item, $style.selection]"
        >
          <DolphinTextInput
            :ref="setDolphinTextInputRef(index)"
            :class="[$style.selection__input, $style.input]"
            placeholder="選択肢を入力..."
            :modelValue="selection"
            :tabIndex="getInputTabIndex(index)"
            :invalid="invalid"
            @update:modelValue="updateSelectionForForm(index, $event)"
            @focus="onInputFocus(index)"
            @blur="onInputBlur"
            @paste="onInputPaste(index, $event)"
            @keypress="onInputKeypress(index, $event)"
            @keydown="onInputKeydown(index, $event)"
          />
          <ButtonClose
            v-if="index > 1"
            :tabIndex="getCloseButtonTabIndex(index)"
            :class="$style.selection__delete"
            @click="deleteSelection(index)"
          />
        </li>
      </ul>
      <p :class="$style.optionalMessage">
        <slot name="optionalMessage" />
      </p>
    </template>
    <footer :class="[$style.container__footer, $style.footer]">
      <button :class="$style.action" @click="appendSelection">
        ＋選択肢を追加
      </button>

      <button
        :class="$style.action"
        @click="onClickInsertTimeSlots"
        v-if="shouldShowTimeSlotPickerButton"
      >
        日程候補を挿入
      </button>
    </footer>
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick } from 'vue'

import ButtonClose from '@/components/atoms/ButtonClose.vue'
import DolphinTextInput from '@/components/atoms/DolphinTextInput.vue'
import ConfirmationModal from '@/components/molecules/ConfirmationModal.vue'
import { useModal } from '@/hooks/useModal'
import { useAlert } from '@/libs/alertPortal'
import {
  useSelectionInputFocus,
  useSelectionManipulator,
  useSelectionTabIndex,
} from './hooks/useSelectionForm'

import { type PickedTimeSlots } from '@/components/organisms/TimeSlotPickerModal/useTimeSlot'

const props = withDefaults(
  defineProps<{
    modelValue: string[]
    shouldShowTimeSlotPickerButton?: boolean
    isDeleteAllSelectionsAllowed?: boolean
    pickedTimeSlots?: PickedTimeSlots
    invalid?: boolean
  }>(),
  {
    isDeleteAllSelectionsAllowed: true,
    invalid: false,
  }
)

const emit = defineEmits<{
  (event: 'update:modelValue', selected: string[]): void
  (event: 'onClickPickTimeSlot'): void
}>()

const { error } = useAlert()

const onClickInsertTimeSlots = () => {
  if (props.modelValue.length > 0) {
    error('選択肢と日程調整は同時に使えません')
    return
  }
  emit('onClickPickTimeSlot')
}

const { ControlledModal, openModal, closeModal } = useModal()

const selections = computed({
  get(): string[] {
    return props.modelValue ?? []
  },
  set(selected: string[]): void {
    emit('update:modelValue', selected)
  },
})

const {
  appendSelection: appendSelectionOriginal,
  deleteAllSelections,
  deleteSelection,
  injectSelection,
  injectSelections,
  updateSelection,
} = useSelectionManipulator(selections)
const appendSelection = computed(() => () => {
  if ((props.pickedTimeSlots ?? []).length > 0) {
    error('選択肢と日程調整は同時に使えません')
    return
  }
  appendSelectionOriginal()
})

const updateSelectionForForm = (index: number, value: string | number) => {
  updateSelection(index, value.toString())
}

const { setDolphinTextInputRef, focusInput } = useSelectionInputFocus()

const { onInputFocus, onInputBlur, getInputTabIndex, getCloseButtonTabIndex } =
  useSelectionTabIndex()

const confirmDeleteAll = () => {
  if (props.modelValue.every(content => content === '')) {
    deleteAllSelections()
    return
  }
  openModal()
}

const submitDeleteAllSelections = () => {
  deleteAllSelections()
  closeModal()
}

const onInputKeypress = async (index: number, event: KeyboardEvent) => {
  if (!(event.target instanceof HTMLInputElement)) return
  if (event.key === 'Enter') {
    // 選択肢編集中に改行した場合はfocusしてる選択肢の次位置に選択肢を挿入し、そこにfocusする
    event.preventDefault()
    const isCursorAtStart =
      event.target.selectionStart === 0 && event.target.selectionEnd === 0
    // カーソルがinputの先頭にあったら一個前にinjectする
    const injectIndex = isCursorAtStart ? index : index + 1
    if (selections.value[injectIndex] !== '') {
      injectSelection(injectIndex)
    }
    await nextTick()
    focusInput(injectIndex)
  }
}

const onInputKeydown = async (index: number, event: KeyboardEvent) => {
  if (!(event.target instanceof HTMLInputElement)) return
  if (event.key === 'Backspace' && event.target.value === '') {
    // 空の選択肢編集中にバックスペースを押下した場合はfocusしてる選択肢を削除し、ひとつ前にfocusする
    event.preventDefault()
    deleteSelection(index)
    await nextTick()
    focusInput(index - 1)
  }
}

const onInputPaste = async (index: number, event: ClipboardEvent) => {
  if (!(event.target instanceof HTMLInputElement)) return
  const pastedRawText = event.clipboardData?.getData('text/plain')
  if (!pastedRawText) {
    return
  }
  const pastedSelections = pastedRawText.split('\n')
  if (pastedSelections.length <= 1) {
    return
  }
  // ペーストされた選択肢が複数行の場合、行ごとに選択肢を挿入する
  event.preventDefault()

  // カーソルがinputの末尾にあった場合、選択肢をその後ろに追加する
  const isCursorAtEnd =
    event.target.selectionStart === event.target.value.length &&
    event.target.value !== ''
  const injectIndex = isCursorAtEnd ? index + 1 : index
  const focusIndex = isCursorAtEnd
    ? index + pastedSelections.length
    : index + pastedSelections.length - 1

  injectSelections(injectIndex, pastedSelections)
  await nextTick()
  focusInput(focusIndex)
}
</script>

<style lang="scss" module>
$input-width: 240px;

.container {
  &__header {
    width: $input-width;
  }

  &__contents {
    &:nth-child(n + 2) {
      margin-top: 4px;
    }
  }

  &__footer {
    &:nth-child(n + 2) {
      margin-top: 4px;
    }
  }
}

.selections {
  &__item {
    &:nth-child(n + 2) {
      margin-top: 4px;
    }
  }
}

.header {
  display: flex;

  &__title {
    flex: auto;
    font-weight: bold;
    line-height: 1.66;
  }

  &__delete {
    color: $color-text-and-object-low-emphasis;
    font-weight: normal;
    line-height: 1.66;
  }
}

.selection {
  &__input {
    width: $input-width;
    font-size: 12px;
  }

  &__delete {
    &:nth-child(n + 2) {
      margin-left: 10px;
    }
  }
}

.action {
  color: $color-text-and-object-accent-primary;
  line-height: 2;

  &:nth-child(n + 1) {
    margin-left: 8px;
  }
}

.optionalMessage {
  margin: 16px 0;
  font-size: 12px;
  color: $color-text-and-object-medium-emphasis;
}
</style>
